import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useContext, useId } from 'react';
import { useValidateFormRequiredProps, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { CheckboxContext } from '../CheckboxContext/index.esm.js';
import { ErrorMessage } from '../ErrorMessage/index.esm.js';
import '@skatteetaten/ds-icons';
import './styles.css';


var styles = {
    "container": "Checkbox-module_container__q1igj",
    "containerSpacingBottom": "Checkbox-module_containerSpacingBottom__W-C1o",
    "input": "Checkbox-module_input__MzmqC",
    "label": "Checkbox-module_label__3VVvm",
    "label_error": "Checkbox-module_label_error__N5UY3",
    "labelCheckbox": "Checkbox-module_labelCheckbox__KY81g",
    "labelCheckboxCheck": "Checkbox-module_labelCheckboxCheck__4MQe3",
    "labelCheckbox_error": "Checkbox-module_labelCheckbox_error__md188",
    "labelContent": "Checkbox-module_labelContent__9yBSH",
    "labelContent_required": "Checkbox-module_labelContent_required__Q3zNk",
    "labelContentDescription": "Checkbox-module_labelContentDescription__x9E29",
    "srOnly": "Checkbox-module_srOnly__JNew1"
};

const Checkbox = /*#__PURE__*/ forwardRef(({ id: idExternal, className = getCommonClassNameDefault(), classNames, lang, 'data-testid': dataTestId, description, errorMessage, checked, defaultChecked, disabled, form, name, required, value, ariaDescribedby, hideLabel, showRequiredMark, onChange, onBlur, onFocus, children }, ref)=>{
    useValidateFormRequiredProps({
        required,
        showRequiredMark
    });
    const context = useContext(CheckboxContext);
    const errorIdExternal = context?.errorId;
    const uniqueInputId = `checkboxInputId-${useId()}`;
    const inputIdInternal = idExternal ?? uniqueInputId;
    const uniqueErrorId = `checkboxErrorId-${useId()}`;
    const errorIdInternal = errorIdExternal ?? uniqueErrorId;
    const hasErrorInternal = errorIdExternal && !checked ? true : !!errorMessage;
    const isRequired = required && !checked;
    const spacingBottomClassName = context ? styles.containerSpacingBottom : '';
    const checkboxErrorClassName = hasErrorInternal ? styles.labelCheckbox_error : '';
    const labelErrorClassName = hasErrorInternal && !context && required ? styles.label_error : '';
    const labelRequiredClassName = !context && showRequiredMark ? styles.labelContent_required : '';
    const hideLabelClassName = hideLabel ? styles.srOnly : '';
    const ariaDescribedbyInput = `${ariaDescribedby ?? ''} ${hasErrorInternal ? errorIdInternal : ''}`.trim();
    return /*#__PURE__*/ jsxs("div", {
        className: `${styles.container} ${spacingBottomClassName} ${className}`.trim(),
        lang: lang,
        children: [
            /*#__PURE__*/ jsx("input", {
                ref: ref,
                id: inputIdInternal,
                className: styles.input,
                "data-testid": dataTestId,
                checked: checked,
                defaultChecked: defaultChecked,
                disabled: disabled,
                form: form,
                name: name,
                required: isRequired,
                type: 'checkbox',
                value: value,
                "aria-describedby": ariaDescribedbyInput || undefined,
                "aria-invalid": hasErrorInternal || undefined,
                onBlur: onBlur,
                onChange: onChange,
                onFocus: onFocus
            }),
            /*#__PURE__*/ jsxs("label", {
                htmlFor: inputIdInternal,
                className: `${styles.label} ${labelErrorClassName} ${classNames?.label ?? ''}`.trim(),
                children: [
                    /*#__PURE__*/ jsx("span", {
                        className: `${styles.labelCheckbox} ${checkboxErrorClassName}`.trim(),
                        children: /*#__PURE__*/ jsx("span", {
                            className: styles.labelCheckboxCheck
                        })
                    }),
                    /*#__PURE__*/ jsxs("span", {
                        className: `${styles.labelContent} ${hideLabelClassName}`.trim(),
                        children: [
                            /*#__PURE__*/ jsxs("span", {
                                className: labelRequiredClassName,
                                children: [
                                    children,
                                    description && /*#__PURE__*/ jsx(Fragment, {
                                        children: " "
                                    })
                                ]
                            }),
                            description && /*#__PURE__*/ jsx("span", {
                                className: styles.labelContentDescription,
                                children: description
                            })
                        ]
                    })
                ]
            }),
            !context && /*#__PURE__*/ jsx(ErrorMessage, {
                id: errorIdInternal,
                className: classNames?.errorMessage,
                showError: hasErrorInternal,
                children: errorMessage
            })
        ]
    });
});
Checkbox.displayName = 'Checkbox';

export { Checkbox };
